if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopMql = window.matchMedia('(min-width: 990px)');
        this.dragClickBlockUntil = 0;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));
          this.initMainImageDrag();
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.initThumbnailDrag();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const currentElement = event?.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === currentElement);
        });

        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${CSS.escape(currentElement.dataset.mediaId)}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        const thumbnailTrack = this.elements.thumbnails.slider;
        if (!thumbnailTrack) return;

        const isVerticalDesktop = this.desktopMql.matches && this.dataset.desktopLayout.includes('thumbnail');

        if (isVerticalDesktop) {
          const visibleTop = thumbnailTrack.scrollTop;
          const visibleBottom = visibleTop + thumbnailTrack.clientHeight;
          const itemTop = thumbnail.offsetTop;
          const itemBottom = itemTop + thumbnail.offsetHeight;

          if (itemTop >= visibleTop + 8 && itemBottom <= visibleBottom - 8) return;

          thumbnailTrack.scrollTo({
            top: Math.max(0, itemTop - 8),
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        thumbnailTrack.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initMainImageDrag() {
        const track = this.elements.viewer?.slider;
        if (!track) return;

        this.mainTrack = track;
        this.mainDragState = null;

        track.addEventListener('pointerdown', this.onMainPointerDown.bind(this));
        track.addEventListener('pointermove', this.onMainPointerMove.bind(this));
        track.addEventListener('pointerup', this.onMainPointerUp.bind(this));
        track.addEventListener('pointercancel', this.onMainPointerCancel.bind(this));
        track.addEventListener('lostpointercapture', this.onMainLostPointerCapture.bind(this));
        track.addEventListener('dragstart', (event) => event.preventDefault());
        track.addEventListener('click', this.blockClickAfterDrag.bind(this), true);
      }

      onMainPointerDown(event) {
        if (!this.desktopMql.matches) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('video, iframe, model-viewer, .deferred-media__poster')) return;

        const items = this.getMainItems();
        if (items.length < 2) return;

        this.mainDragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startScrollLeft: this.mainTrack.scrollLeft,
          startIndex: this.getClosestMainIndex(this.mainTrack.scrollLeft, items),
          dragging: false,
          cancelled: false,
        };

        this.mainTrack.setPointerCapture?.(event.pointerId);
      }

      onMainPointerMove(event) {
        const state = this.mainDragState;
        if (!state || event.pointerId !== state.pointerId || state.cancelled) return;

        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;

        if (!state.dragging && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
          state.cancelled = true;
          this.finishMainPointer(false);
          return;
        }

        if (!state.dragging && Math.abs(deltaX) > 5 && Math.abs(deltaX) > Math.abs(deltaY)) {
          state.dragging = true;
          this.mainTrack.style.scrollBehavior = 'auto';
          this.mainTrack.style.scrollSnapType = 'none';
          this.mainTrack.style.cursor = 'grabbing';
        }

        if (!state.dragging) return;

        event.preventDefault();
        const maxScroll = Math.max(0, this.mainTrack.scrollWidth - this.mainTrack.clientWidth);
        const requested = state.startScrollLeft - deltaX;
        this.mainTrack.scrollLeft = Math.max(0, Math.min(requested, maxScroll));
      }

      onMainPointerUp(event) {
        const state = this.mainDragState;
        if (!state || event.pointerId !== state.pointerId) return;

        const items = this.getMainItems();
        const deltaX = event.clientX - state.startX;
        let targetIndex = state.startIndex;

        if (state.dragging) {
          const threshold = Math.min(100, Math.max(40, this.mainTrack.clientWidth * 0.12));
          if (Math.abs(deltaX) >= threshold) {
            targetIndex = state.startIndex + (deltaX < 0 ? 1 : -1);
          } else {
            targetIndex = this.getClosestMainIndex(this.mainTrack.scrollLeft, items);
          }

          targetIndex = Math.max(0, Math.min(targetIndex, items.length - 1));
          this.dragClickBlockUntil = performance.now() + 450;
        }

        const wasDragging = state.dragging;
        this.finishMainPointer(wasDragging);

        if (wasDragging && items[targetIndex]) {
          this.settleMainTo(items[targetIndex]);
        }
      }

      onMainPointerCancel(event) {
        const state = this.mainDragState;
        if (!state || event.pointerId !== state.pointerId) return;

        const items = this.getMainItems();
        const target = items[state.startIndex];
        const wasDragging = state.dragging;
        this.finishMainPointer(wasDragging);
        if (wasDragging && target) this.settleMainTo(target);
      }

      onMainLostPointerCapture() {
        if (!this.mainDragState) return;

        const state = this.mainDragState;
        const items = this.getMainItems();
        const target = items[state.startIndex];
        const wasDragging = state.dragging;
        this.finishMainPointer(wasDragging, false);
        if (wasDragging && target) this.settleMainTo(target);
      }

      finishMainPointer(wasDragging, releaseCapture = true) {
        const state = this.mainDragState;
        if (!state) return;

        const pointerId = state.pointerId;
        this.mainDragState = null;

        if (releaseCapture && this.mainTrack.hasPointerCapture?.(pointerId)) {
          this.mainTrack.releasePointerCapture(pointerId);
        }

        this.mainTrack.style.cursor = '';

        if (!wasDragging) {
          this.mainTrack.style.scrollBehavior = '';
          this.mainTrack.style.scrollSnapType = '';
        }
      }

      settleMainTo(targetItem) {
        if (!targetItem) return;

        this.mainTrack.style.scrollBehavior = 'smooth';
        this.mainTrack.style.scrollSnapType = 'x mandatory';
        this.mainTrack.scrollTo({ left: targetItem.offsetLeft, behavior: 'smooth' });

        window.setTimeout(() => {
          this.mainTrack.style.scrollBehavior = '';
          this.mainTrack.style.scrollSnapType = '';
          this.elements.viewer.update?.();

          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
            item.classList.toggle('is-active', item === targetItem);
          });

          const thumbnail = this.elements.thumbnails?.querySelector(
            `[data-target="${CSS.escape(targetItem.dataset.mediaId)}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }, 360);
      }

      blockClickAfterDrag(event) {
        if (performance.now() > this.dragClickBlockUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      getMainItems() {
        return Array.from(this.mainTrack?.querySelectorAll('[data-media-id]') || []).filter(
          (item) => item.offsetParent !== null
        );
      }

      getClosestMainIndex(scrollLeft, items = this.getMainItems()) {
        if (!items.length) return 0;

        let index = 0;
        let distance = Number.POSITIVE_INFINITY;

        items.forEach((item, itemIndex) => {
          const nextDistance = Math.abs(item.offsetLeft - scrollLeft);
          if (nextDistance < distance) {
            distance = nextDistance;
            index = itemIndex;
          }
        });

        return index;
      }

      initThumbnailDrag() {
        const track = this.elements.thumbnails?.slider;
        if (!track) return;

        this.thumbnailTrack = track;
        this.thumbnailDragState = null;
        this.thumbnailClickBlockUntil = 0;

        track.addEventListener('pointerdown', this.onThumbnailPointerDown.bind(this));
        track.addEventListener('pointermove', this.onThumbnailPointerMove.bind(this));
        track.addEventListener('pointerup', this.onThumbnailPointerUp.bind(this));
        track.addEventListener('pointercancel', this.onThumbnailPointerCancel.bind(this));
        track.addEventListener('lostpointercapture', this.onThumbnailLostPointerCapture.bind(this));
        track.addEventListener('click', this.blockThumbnailClickAfterDrag.bind(this), true);
        track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      onThumbnailPointerDown(event) {
        if (!this.desktopMql.matches || !this.dataset.desktopLayout.includes('thumbnail')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.thumbnailDragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startScrollTop: this.thumbnailTrack.scrollTop,
          dragging: false,
        };

      }

      onThumbnailPointerMove(event) {
        const state = this.thumbnailDragState;
        if (!state || event.pointerId !== state.pointerId) return;

        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;

      if (!state.dragging && Math.abs(deltaY) > 5 && Math.abs(deltaY) > Math.abs(deltaX)) {
        state.dragging = true;
        this.thumbnailTrack.setPointerCapture?.(event.pointerId);
        this.thumbnailTrack.style.scrollBehavior = 'auto';
          this.thumbnailTrack.style.scrollSnapType = 'none';
          this.thumbnailTrack.style.cursor = 'grabbing';
        }

        if (!state.dragging) return;

        event.preventDefault();
        const maxScroll = Math.max(0, this.thumbnailTrack.scrollHeight - this.thumbnailTrack.clientHeight);
        const requested = state.startScrollTop - deltaY;
        this.thumbnailTrack.scrollTop = Math.max(0, Math.min(requested, maxScroll));
      }

      onThumbnailPointerUp(event) {
        const state = this.thumbnailDragState;
        if (!state || event.pointerId !== state.pointerId) return;

        const wasDragging = state.dragging;
        if (wasDragging) this.thumbnailClickBlockUntil = performance.now() + 350;
        this.finishThumbnailPointer(wasDragging);
      }

      onThumbnailPointerCancel(event) {
        const state = this.thumbnailDragState;
        if (!state || event.pointerId !== state.pointerId) return;
        this.finishThumbnailPointer(state.dragging);
      }

      onThumbnailLostPointerCapture() {
        if (!this.thumbnailDragState) return;
        this.finishThumbnailPointer(this.thumbnailDragState.dragging, false);
      }

      finishThumbnailPointer(wasDragging, releaseCapture = true) {
        const state = this.thumbnailDragState;
        if (!state) return;

        const pointerId = state.pointerId;
        this.thumbnailDragState = null;

        if (releaseCapture && this.thumbnailTrack.hasPointerCapture?.(pointerId)) {
          this.thumbnailTrack.releasePointerCapture(pointerId);
        }

        this.thumbnailTrack.style.cursor = '';
        this.thumbnailTrack.style.scrollBehavior = '';
        this.thumbnailTrack.style.scrollSnapType = '';

        if (wasDragging) {
          this.thumbnailTrack.style.scrollSnapType = 'y mandatory';
        }
      }

      blockThumbnailClickAfterDrag(event) {
        if (performance.now() > this.thumbnailClickBlockUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
  );
}
